import { Button, Classes, Colors, FileInput, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { getIn } from "formik";
import {
  FGCustomInput,
  FieldGroup,
  FlexFieldGroup,
  IFGCustomInputProps,
  showError,
  useFGContext,
} from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { DownloadButton, MessageBubble } from "..";
import { DocumentDto, FileResponse } from "../../api";
import { useCommonHooks } from "../../hooks";

import { downloadFileResponse } from "../../utils/downloadFileResponse";

const StyledFileInput = styled(FileInput)`
  & .${Classes.FILE_UPLOAD_INPUT} {
    color: ${Colors.BLACK} !important;
  }
`;

export interface FGFileInputProps
  extends Omit<IFGCustomInputProps, "children"> {
  downloadFn?: () => Promise<FileResponse>;
  removeFileFn?: () => void;
  validationErrors?: any;
  validationErrorsKey?: string;
  disabled?: boolean;
}

export const FGFileInput: React.FunctionComponent<FGFileInputProps> = ({
  name,
  downloadFn = null,
  removeFileFn = null,
  validationErrors,
  validationErrorsKey,
  disabled = false,
  ...customInputProps
}) => {
  const { t } = useCommonHooks();
  const { formik } = useFGContext();

  const [downloading, setdownloading] = React.useState(false);

  const file = React.useMemo(() => {
    const data = getIn(formik?.values, name);
    if (!data) return null;
    if (!(data instanceof DocumentDto))
      throw new Error(
        `The value of FGFileInput must be an object containing a property named "fileName" or containing "FileName" (Field: ${name})`
      );
    return data;
  }, [formik?.values, name]);

  const downloadDocument = React.useCallback(async () => {
    try {
      setdownloading(true);
      const file = await downloadFn();
      await downloadFileResponse(file);
    } catch (err: any) {
      console.log("Error: ", err);
      showError(t("Error"));
    }
    setdownloading(false);
  }, [downloadFn, t]);

  const onChange = React.useCallback(
    (e) => {
      if (e.target.files.length > 0) {
        const reader = new FileReader();
        reader.addEventListener("load", () => {
          const filePathSplit = e.target.value.split("\\");
          const newFile = new DocumentDto({
            ...getIn(formik?.values, name),
            fileName: filePathSplit[filePathSplit.length - 1],
            contentB64: reader.result.toString(),
          });

          formik.setFieldTouched(`${name}.fileName`, true);
          formik.setFieldValue(name, newFile, true);
        });
        reader.readAsDataURL(e.target.files[0]);
      } else {
        formik.setFieldTouched(`${name}.fileName`, true);
        formik.setFieldValue(name, null, true);
      }
    },
    [formik, name]
  );

  return (
    <FGCustomInput name={`${name}.fileName`} {...customInputProps}>
      {() => (
        <>
          {validationErrors && validationErrors[validationErrorsKey] && (
            <FlexFieldGroup>
              <MessageBubble
                intent={Intent.DANGER}
                innerMessage={validationErrors[validationErrorsKey]}
              />
            </FlexFieldGroup>
          )}
          <FieldGroup columns={[10, 1, 1]}>
            <StyledFileInput
              text={file?.fileName ?? t("ChooseAFile")}
              onInputChange={onChange}
              buttonText={t("Browse")}
              fill={true}
              disabled={disabled}
              inputProps={{ accept: "application/pdf" }}
            />
            {downloadFn && file !== null && (
              //   <CanDoAction actions={downloadActions}>
              <DownloadButton
                onClick={downloadDocument}
                loading={downloading}
              />
              //</CanDoAction>
            )}
            {removeFileFn && file !== null && (
              <Button
                onClick={removeFileFn}
                icon={IconNames.TRASH}
                minimal
                intent={Intent.DANGER}
                // disabled={disabled}
              />
            )}
          </FieldGroup>
        </>
      )}
    </FGCustomInput>
  );
};

export default FGFileInput;
